/**
 * 중점 분석 «영역» 그리기 표면 — 사진 한 장 위에 사각형 하나를 그리고 옮기고 크기를 바꾼다.
 *
 * ⚠️ **`InteractiveHazardEditor`에 `onCreate`를 더하는 대신 따로 둔 것은 네 가지 코드 사실
 * 때문이다**(줄기 manifest 설계표의 「`onCreate` 추가」와 다른 판단, 2026-10-05 P5):
 *
 * 1. 그 컴포넌트는 `hazards.length === 0`이면 **`null`을 반환**한다. 업로드 미리보기는 발견사항이
 *    0이라 영영 뜨지 않고, 그 가드를 풀면 기존 호스트 전부의 동작이 바뀐다.
 * 2. 사진 상세에서는 **발견사항 박스와 영역이 동시에** 떠야 하는데, 그쪽 배경은 이미
 *    `handleBackgroundClick`(선택 해제)이 쓴다 — 배경 드래그=영역 생성을 얹으면 해제와 충돌한다.
 * 3. props가 번호·점수·`riskConfig`·「위험 영역 삭제」 확인까지 전부 hazard 도메인이다.
 * 4. 영역은 `{x,y,w,h}`, 발견사항 박스는 `{x,y,width,height}`다. 두 어휘를 한 컴포넌트에 담는 것이
 *    이 작업에서 가장 피해야 할 자리다(틀리면 서버가 400을 내거나 조용히 전체 분석이 된다).
 *
 * **그 대신 좌표 변환은 한 벌로 모았다** — 영역 수학은 전부 `region-geometry.ts`(순수·spec 보유)에
 * 있고, object-contain 여백 계산은 shared `measureRenderedImageBox`가 소유한다(아래 `updateFit`).
 * 두 진입(업로드 미리보기·사진 상세)이 이 컴포넌트를 재사용하므로 변환 사본이 늘지 않는다.
 *
 * ⚠️ 여기서 만드는 좌표는 **표시 방향 기준**이다 — 브라우저가 EXIF 회전을 적용해 그린 그 방향이고,
 * 서버의 영역 마커 합성이 `sharp.rotate()` 뒤에 일어나 두 좌표계가 맞춰져 있다. 원본 픽셀
 * 격자로 환산하지 말 것.
 *
 * ## 왜 이 컴포넌트에 거동 spec이 없나 — 원리적으로 불가능하다
 *
 * 아래 포인터 표면(`surfaceRef` div)은 **`fit.width > 0 && fit.height > 0`일 때만 렌더**되고
 * `fit`은 `image.naturalWidth`·`container.getBoundingClientRect()`에서 나온다. jsdom은 레이아웃을
 * 계산하지 않아 **그 둘이 늘 0**이므로, 테스트 환경에서는 표면이 **렌더조차 되지 않는다** —
 * 드래그·포인터 캡처·종료 폴백을 관측할 경로가 없다. 그래서 이 디렉토리의 spec은 순수 수학
 * (`region-geometry.test.ts`)과 토글 규칙(`region-on-ai-toggle.test.ts`)뿐이다.
 *
 * ⚠️ 그러니 **없는 것을 억지로 꾸미지 말 것** — 표면을 우회해 내부 상태를 직접 찔러 만든 단언은
 * 다음 사람이 「이 축은 검사가 지킨다」의 근거로 쓰는데 실제로는 아무것도 지키지 않는다.
 * 이 축(포인터 조작·좌표 되매핑)의 탐지기는 **dev 브라우저 실측**이고, 기록은
 * `docs/verification/`에 있다. 바꿀 때는 거기서 다시 재라.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { measureRenderedImageBox, type AnalysisRegion } from '@safetyhub/shared';
import {
  moveRegion,
  regionFromDragPoints,
  resizeRegion,
  toNormalizedPoint,
  type NormalizedPoint,
  type RegionCorner,
} from './region-geometry';

export interface AnalysisRegionEditorProps {
  /** 사진 URL (업로드 전이면 `URL.createObjectURL` 결과) */
  imageUrl: string;
  /** 현재 영역 — `null`이면 전체 분석(영역 없음) */
  value: AnalysisRegion | null;
  /** 영역 변경 — 드래그가 끝날 때만 부른다(드래그 중 상태는 내부가 들고 있다) */
  onChange: (region: AnalysisRegion | null) => void;
  /** 사진 대체 텍스트 */
  alt?: string;
}

/** 드래그 중인 조작 — 새로 그리기 / 이동 / 모서리 리사이즈 */
type Gesture =
  | { kind: 'create'; from: NormalizedPoint }
  | { kind: 'move'; startPoint: NormalizedPoint; startRegion: AnalysisRegion }
  | { kind: 'resize'; corner: RegionCorner };

const HANDLE_RADIUS = 7;

/**
 * 측정 불가일 때의 fit — **0이어야 한다.** 아래 포인터 표면은 `fit.width > 0`일 때만 렌더되므로
 * 0은 「아무것도 그리지 않는다」가 되고, 그것이 측정 전에 박스를 엉뚱한 자리에 얹는 것보다 낫다
 * (공용 유틸이 `null`을 돌려주는 이유와 같은 판단 — 「조용한 오표시보다 시끄러운 미표시」).
 */
const ZERO_FIT = { width: 0, height: 0, offsetX: 0, offsetY: 0 };

export function AnalysisRegionEditor({
  imageUrl,
  value,
  onChange,
  alt = '분석 영역을 지정할 사진',
}: AnalysisRegionEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  /**
   * SVG `mask` id — **고정 문자열이면 안 된다.** SVG id는 문서 전역이라 이 에디터가 한 화면에
   * 두 벌 뜨는 순간 `url(#...)`이 먼저 정의된 쪽을 집어 어둡게 처리가 **엉뚱한 사각형**을
   * 따라간다. 지금은 두 진입(업로드 시트·사진 상세) 모두 전체 화면 오버레이라 동시 개방
   * 경로가 없지만, 그 사실이 깨질 때 증상이 조용하고 원인이 멀어서 미리 막는다(2026-10-05).
   */
  const instanceId = useId();
  // `useId()` 값에는 콜론이 섞인다(`:r1:`) — `url(#...)` 참조는 id 조회라 그대로도 동작하지만,
  // 셀렉터로 그 id를 찾으려는 코드(devtools·스냅샷 도구)에서 깨지므로 걷어낸다.
  const maskId = `analysis-region-mask-${instanceId.replace(/:/g, '')}`;

  /** 사진이 실제로 그려진 사각형 — object-contain 여백을 뺀 값 */
  const [fit, setFit] = useState(ZERO_FIT);
  const [gesture, setGesture] = useState<Gesture | null>(null);
  /** 드래그 중 미리보기 — 확정 전에는 onChange를 부르지 않는다 */
  const [draft, setDraft] = useState<AnalysisRegion | null>(null);
  /**
   * 조작이 진행 중인지 — **종료를 한 번만** 처리하기 위한 동기 플래그.
   *
   * `gesture` state로는 못 막는다: surface의 `onPointerUp`과 아래 window 폴백이 **같은 렌더의
   * 클로저**를 보므로 둘 다 같은 값을 읽고 `onChange`가 두 번 불린다.
   */
  const activeRef = useRef(false);

  /**
   * object-contain 표시 영역 계산 — **수식은 이 파일이 아니라 shared `measureRenderedImageBox`가
   * 소유한다**(`packages/shared/src/utils/analysis-region-render.ts`, 값 spec 보유). 종전에는 이
   * 파일과 `PhotoViewerWithEditor`·`PhotoCardVertical`에 같은 수식이 세 벌 있었다(2026-10-08 P4-l).
   *
   * ⚠️ **이 자리의 거동 spec은 원리적으로 불가능하다** — 아래 포인터 표면은 `fit.width > 0`일 때만
   * 렌더되고 `fit`은 `getBoundingClientRect()`·`naturalWidth`에서 나오는데 **jsdom은 레이아웃을
   * 계산하지 않아 그 둘이 늘 0**이다. 즉 이 변환이 틀려도 깨지는 테스트가 없다. 기계로 잴 수 있는
   * 것은 **값 층**(그 함수의 짝 spec)뿐이고, 「박스가 사진 그 자리에 얹히는가」의 판정은
   * **브라우저 실측**이다(선례: `docs/verification/2026-10-08-bbox-browser-measure/`).
   * 여기를 바꾸면 거기서 다시 재라 — 초록을 근거로 쓰지 말 것.
   *
   * ⚠️ 요소 크기는 `getBoundingClientRect()`로 잰다 — `clientWidth`는 **정수로 반올림**되어 상자와
   * 이미지의 비율이 같은 자리에서도 두 배율을 어긋나게 만든다(그 함수 JSDoc의 경고). 그 축은
   * 배선 래칫이 지킨다(`__tests__/bboxMeasureWiring.test.ts`).
   *
   * ⚠️ **그런데 재는 것은 `<img>`가 아니라 «컨테이너»다 — 그것이 맞는 이유가 CSS 한 줄에 있다.**
   * 공용 함수는 「이미지 **요소**의 렌더 크기」를 요구하는데, 여기서 컨테이너를 넘겨도 되는 것은
   * 아래 `<img>`가 **`absolute inset-0 w-full h-full`**이라 **두 상자가 정확히 같기** 때문이다.
   * 그 클래스를 벗기거나(예: `object-contain`을 유지한 채 `w-auto`로) 바꾸면 **이 함수는 그대로인데
   * 박스가 틀린다** — 그때 고칠 곳은 이 입력(그 `<img>`의 rect로)이다. 타입도 spec도 이 의존을
   * 보지 못하고 jsdom은 레이아웃이 없어 못 잰다(판정은 브라우저 실측).
   */
  const updateFit = useCallback(() => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image || !image.naturalWidth || !image.naturalHeight) return;

    const rect = container.getBoundingClientRect();
    const box = measureRenderedImageBox({
      elementWidth: rect.width,
      elementHeight: rect.height,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      objectFit: 'contain',
    });

    if (!box) {
      // 컨테이너가 아직(또는 숨김 전환 중) 0×0 — 종전 수식도 이 입력에서 0을 내놓아 표면이
      // 사라졌다. 낡은 박스를 남기면 0×0 상자 밖으로 삐져나와 엉뚱한 자리를 가리킨다.
      setFit(prev => (prev.width === 0 && prev.height === 0 ? prev : ZERO_FIT));
      return;
    }

    setFit({ width: box.width, height: box.height, offsetX: box.left, offsetY: box.top });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(updateFit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [updateFit]);

  /**
   * 포인터 → 정규화 좌표.
   *
   * `surfaceRef`의 rect를 쓴다 — 그 요소가 **사진 사각형과 정확히 겹치게** 배치돼 있으므로
   * (`left: offsetX, top: offsetY, width/height = fit`) 여백 보정이 좌표 계산에 녹아 있다.
   * 컨테이너 rect를 쓰면 여백만큼 밀려 사진 밖을 가리키는 영역이 만들어진다.
   */
  const pointFromEvent = useCallback((e: React.PointerEvent): NormalizedPoint | null => {
    const surface = surfaceRef.current;
    if (!surface) return null;
    return toNormalizedPoint(surface.getBoundingClientRect(), e.clientX, e.clientY);
  }, []);

  const beginGesture = useCallback(
    (e: React.PointerEvent, next: Gesture) => {
      e.stopPropagation();
      e.preventDefault();
      try {
        (e.target as Element).setPointerCapture(e.pointerId);
      } catch {
        /*
         * 포인터 캡처는 보조 수단이다 — **다만 「실패해도 pointermove/up은 계속 온다」는 틀렸다**
         * (2026-10-05 리뷰 정정, 종전 주석이 그렇게 적고 있었다).
         *
         * `onPointerMove`/`onPointerUp`은 surface div에만 달려 있으므로, 캡처가 실패한 상태에서
         * 포인터를 **사진 밖에서** 떼면 up이 이 컴포넌트에 닿지 않아 `gesture`가 남는다. 그러면
         * 그 뒤로는 버튼을 누르지 않았는데도 커서 이동만으로 사각형이 계속 따라온다.
         * 그래서 아래 effect가 **window에 pointerup/pointercancel 폴백**을 건다 —
         * 종료 경로는 둘이지만 `activeRef`가 한 번만 처리되게 보장한다.
         */
      }
      activeRef.current = true;
      setGesture(next);
      setDraft(value);
    },
    [value],
  );

  const handlePointerDownSurface = useCallback(
    (e: React.PointerEvent) => {
      const point = pointFromEvent(e);
      if (!point) return;
      beginGesture(e, { kind: 'create', from: point });
      // 새로 그리기 시작 — 아직 최소 크기를 못 넘겼으니 미리보기는 비운다.
      setDraft(null);
    },
    [beginGesture, pointFromEvent],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!gesture) return;
      const point = pointFromEvent(e);
      if (!point) return;

      if (gesture.kind === 'create') {
        setDraft(regionFromDragPoints(gesture.from, point));
        return;
      }
      if (gesture.kind === 'move') {
        setDraft(
          moveRegion(
            gesture.startRegion,
            point.x - gesture.startPoint.x,
            point.y - gesture.startPoint.y,
          ),
        );
        return;
      }
      setDraft((prev) => (prev ? resizeRegion(prev, gesture.corner, point) : prev));
    },
    [gesture, pointFromEvent],
  );

  const endGesture = useCallback(() => {
    // ⚠️ `gesture`가 아니라 **ref**로 가드한다 — surface의 onPointerUp과 window 폴백이 같은
    // 렌더의 클로저를 보므로 state로 막으면 둘 다 통과해 `onChange`가 두 번 불린다.
    if (!activeRef.current) return;
    activeRef.current = false;
    setGesture(null);
    // 새로 그리다 최소 크기를 못 넘기면 **직전 영역을 지우지 않는다** — 사진을 한 번 눌렀다고
    // 지정해 둔 영역이 사라지면 그것은 사용자가 의도한 「해제」가 아니다(해제는 전용 버튼이다).
    if (draft) onChange(draft);
    setDraft(null);
  }, [draft, onChange]);

  /**
   * 조작 종료의 **폴백** — 포인터를 사진 밖에서 떼도 끝난다.
   *
   * 캡처가 성공하면 surface의 `onPointerUp`이 먼저 돌고, 실패했거나 포인터가 밖에서 떼어지면
   * 이 리스너가 돈다. 둘 중 무엇이 먼저 와도 `endGesture`는 `activeRef` 덕에 한 번만 처리된다.
   *
   * 구독은 **드래그 중에만** 한다(`dragging`) — `endGesture`는 `draft`가 바뀔 때마다 새로
   * 만들어지므로 그것을 deps에 넣으면 pointermove마다 리스너를 다시 달게 된다. 그래서 최신
   * 콜백은 ref로 읽는다.
   */
  const endGestureRef = useRef(endGesture);
  useEffect(() => {
    endGestureRef.current = endGesture;
  }, [endGesture]);

  const dragging = gesture !== null;
  useEffect(() => {
    if (!dragging) return;
    const finish = () => endGestureRef.current();
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => {
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };
  }, [dragging]);

  /** 드래그 중이면 미리보기, 아니면 확정값 */
  const shown: AnalysisRegion | null = gesture ? draft : value;

  // 정규화 → 표시 픽셀.
  const px =
    shown !== null
      ? {
          x: shown.x * fit.width,
          y: shown.y * fit.height,
          w: shown.w * fit.width,
          h: shown.h * fit.height,
        }
      : null;

  const corners: Array<{ corner: RegionCorner; cx: number; cy: number; cursor: string }> =
    px === null
      ? []
      : [
          { corner: 'tl', cx: px.x, cy: px.y, cursor: 'nwse-resize' },
          { corner: 'tr', cx: px.x + px.w, cy: px.y, cursor: 'nesw-resize' },
          { corner: 'bl', cx: px.x, cy: px.y + px.h, cursor: 'nesw-resize' },
          { corner: 'br', cx: px.x + px.w, cy: px.y + px.h, cursor: 'nwse-resize' },
        ];

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-ink-900 overflow-hidden select-none"
      // ⚠️ 터치 제스처 축의 소유자는 **아래 surface**다(`touchAction: 'none'` 고정). 여기에
      // `gesture ? 'none' : 'auto'`로 **조건부**를 두었던 것이 2026-10-06 폴드 제보의 원인이다 —
      // 브라우저는 **첫 `touchmove` 시점의 `touch-action` 값**으로 그 터치를 스크롤·팬으로
      // 가져갈지 정하는데, 그 시점엔 아직 `gesture`가 false라 `auto`였다. 그래서 브라우저가
      // 터치를 스크롤로 채 가고 `onPointerCancel`이 돌아 **영역이 아예 생기지 않았다**(마우스는
      // 이 규칙이 없어 PC만 정상이었다 — 「PC는 되는데 모바일만」의 전형이다).
      // 컨테이너는 `auto`로 둔다 — 레터박스 여백에서 시작한 터치는 모달 스크롤로 쓰여야 한다.
    >
      <img
        ref={imageRef}
        src={imageUrl}
        alt={alt}
        onLoad={updateFit}
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        draggable={false}
      />

      {fit.width > 0 && fit.height > 0 && (
        <div
          ref={surfaceRef}
          className="absolute cursor-crosshair"
          // ⚠️ `touchAction: 'none'`은 **고정이어야 한다 — 조건부로 켜면 늦다.** 이 값은
          // 제스처가 시작되기 «전»에 이미 `none`이어야 브라우저가 그 터치를 스크롤로 가져가지
          // 않는다(위 컨테이너 주석의 경위). 자손인 리사이즈 핸들도 교차 규칙으로 함께 덮인다.
          style={{
            left: fit.offsetX,
            top: fit.offsetY,
            width: fit.width,
            height: fit.height,
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDownSurface}
          onPointerMove={handlePointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
        >
          <svg width={fit.width} height={fit.height} className="absolute inset-0">
            {/* 영역 밖을 어둡게 — 「AI가 무엇을 볼지」가 한눈에 보인다. 영역이 없으면 그리지 않는다
                (사진 전체가 분석 대상이라 어둡게 할 밖이 없다). */}
            {px && (
              <>
                <defs>
                  <mask id={maskId}>
                    <rect x={0} y={0} width={fit.width} height={fit.height} fill="white" />
                    <rect x={px.x} y={px.y} width={px.w} height={px.h} fill="black" />
                  </mask>
                </defs>
                <rect
                  x={0}
                  y={0}
                  width={fit.width}
                  height={fit.height}
                  fill="rgba(0,0,0,0.5)"
                  mask={`url(#${maskId})`}
                  className="pointer-events-none"
                />
                <rect
                  x={px.x}
                  y={px.y}
                  width={px.w}
                  height={px.h}
                  fill="transparent"
                  stroke="#ffffff"
                  strokeWidth={2}
                  className="cursor-move"
                  onPointerDown={(e) => {
                    const point = pointFromEvent(e);
                    if (!point || !shown) return;
                    beginGesture(e, { kind: 'move', startPoint: point, startRegion: shown });
                  }}
                />
                {corners.map(({ corner, cx, cy, cursor }) => (
                  <circle
                    key={corner}
                    cx={cx}
                    cy={cy}
                    r={HANDLE_RADIUS}
                    fill="#ffffff"
                    stroke="#111827"
                    strokeWidth={2}
                    style={{ cursor }}
                    onPointerDown={(e) => beginGesture(e, { kind: 'resize', corner })}
                  />
                ))}
              </>
            )}
          </svg>
        </div>
      )}

      {/* 영역이 없을 때만 안내 — 존재가 상태를 뜻하므로 조건부 렌더로 둔다(숨김 class가 아니다). */}
      {shown === null && !gesture && (
        <div className="absolute inset-x-0 bottom-0 p-3 pointer-events-none">
          <p className="text-center text-xs text-white bg-black/60 rounded-control py-1.5 px-2">
            사진을 드래그해 분석할 범위를 지정하세요. 지정하지 않으면 사진 전체를 분석합니다.
          </p>
        </div>
      )}
    </div>
  );
}

export default AnalysisRegionEditor;
